interface CacheItem {
    timestamp: number;
    data: any;
}

const responseCache: Record<string, CacheItem> = {};

export const hasValidCache = (url: string, cacheDurationMs: number): boolean => {
    const cacheItem = responseCache[url];
    if (!cacheItem) return false;

    const currentTime = Date.now();
    return currentTime - cacheItem.timestamp < cacheDurationMs;
};

export const getCache = (url: string): any => {
    return responseCache[url]?.data;
};

export const setCache = (url: string, data: any): void => {
    responseCache[url] = {
        timestamp: Date.now(),
        data,
    };
};

// Empties the whole cache. Called on logout (so the next user is never served the
// previous user's payloads) and on any successful mutation / explicit refresh (so
// stale reads don't survive a write).
export const clearCache = (): void => {
    for (const url of Object.keys(responseCache)) {
        delete responseCache[url];
    }
};

// Which relative URLs are cached. The cache is a deliberate rate-limiter against
// repeat-navigation API slamming — kept by product decision — so this list stays
// tight. `/customers/\d+$` is ANCHORED: it caches the customer-detail resource but
// NOT its sub-collections (`/customers/{id}/proformas`, `/customers/{id}/upcoming-events`),
// which are volatile and were being wrongly served stale.
interface CacheConfig { url: RegExp; duration?: number; }
const cacheConfig: CacheConfig[] = [
    { url: /customers$/ },
    { url: /customers\/\d+$/ },
    { url: /bookings$/ },
    { url: /bookings\?customer_id=\d+/ },
    { url: /profile-packages$/ },
    { url: /profile-packages\?customer_id=\d+/ },
    { url: /financial-transactions\?customer_id=\d+/ },
    { url: /communications\?customer_id=\d+/ },
];

export const shouldIncludeInCache = (url: string): boolean => {
    return cacheConfig.some((cnf) => cnf.url.test(url));
};

// Marks a request config whose response is produced by the cache adapter (a cache
// HIT) rather than the network, so the response policy can tell the two apart.
const SERVED_FROM_CACHE = "__servedFromCache";

export const markServedFromCache = (config: any): void => {
    config[SERVED_FROM_CACHE] = true;
};

const wasServedFromCache = (response: any): boolean => {
    return Boolean(response?.config?.[SERVED_FROM_CACHE]);
};

// The write/evict half of the cache, applied to every successful response:
//  - a fresh network GET is stored (a cache HIT is NOT re-stored, so the TTL stays
//    absolute from the first fetch rather than sliding forward on every hit);
//  - any successful non-GET is a mutation: it may invalidate an unknown set of
//    cached reads (a booking write touches bookings, profile-packages and upcoming
//    events), so the whole read cache is dropped rather than guessing which entries.
export const applyCacheResponsePolicy = (response: any): void => {
    const method = response?.config?.method;
    const url = response?.config?.url as string;

    if (method === "get") {
        if (!wasServedFromCache(response)) {
            setCache(url, response.data);
        }
        return;
    }

    clearCache();
};
